import { Injectable } from '@angular/core';
import { from } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { getTimeZones } from '@vvo/tzdb';
import * as moment from 'moment';

/**
 * Represents a timezone option for UI display
 * @interface TimeZoneOption
 */
export interface TimeZoneOption {
  /** Timezone identifier (e.g., 'America/New_York') */
  value: string;
  /** Human-readable display label with GMT offset and city */
  label: string;
  /** Optional timezone group for categorization */
  group?: string[];
}

/**
 * Service for managing timezone data and operations.
 *
 * Provides a reactive stream of timezone options formatted for UI consumption.
 * Uses lazy loading and caching for optimal performance.
 *
 * @injectable Root level service
 */
@Injectable({ providedIn: 'root' })
export class TimezoneService {
  /**
   * Observable stream of timezone options, sorted by GMT offset.
   *
   * Features:
   * - Lazy loading: Calculation deferred until first subscription
   * - Automatic caching: Results cached via shareReplay(1)
   * - Sorted by GMT offset, then alphabetically by timezone name
   * - Dynamic import for code splitting optimization
   *
   * @readonly Observable that emits timezone options array
   */
  readonly list$ = from(import('@vvo/tzdb')).pipe(
    // Dynamically import timezone data for code splitting
    map(() => getTimeZones()),
    // Transform and sort timezone data for UI consumption
    map((zones) =>
      zones
        .sort(
          (a, b) =>
            // Primary sort: GMT offset (earliest to latest)
            a.currentTimeOffsetInMinutes - b.currentTimeOffsetInMinutes ||
            // Secondary sort: Alphabetical by timezone name
            a.alternativeName.localeCompare(b.alternativeName) ||
            // Tertiary sort: Alphabetical by city name
            a.mainCities[0].localeCompare(b.mainCities[0]),
        )
        .map<TimeZoneOption>((z) => this.transformToTimeZoneOption(z)),
    ),
    // Cache result for all subscribers (prevents re-computation)
    shareReplay(1),
  );

  /**
   * Transforms raw timezone data into UI-friendly option format.
   *
   * Creates a formatted label showing GMT offset, timezone name, and primary city.
   * Example: "(GMT-05:00) Eastern Standard Time - New York"
   *
   * @private
   * @param zone Raw timezone data from @vvo/tzdb
   * @returns Formatted timezone option for UI display
   */
  private transformToTimeZoneOption(zone: any): TimeZoneOption {
    // Calculate and format GMT offset (e.g., "+05:00", "-08:00")
    const gmtOffset = moment().utcOffset(zone.currentTimeOffsetInMinutes).format('Z');

    // Build human-readable label with GMT offset, name, and primary city
    const label = `(GMT${gmtOffset}) ${zone.alternativeName} - ${zone.mainCities[0]}`;

    return {
      value: zone.name,
      label,
      group: zone.group,
    };
  }
}
